import { css } from '@emotion/css';
import peachjarLogo from '@/assets/peachjar-logo.svg';
import sheetsSaved from '@/assets/sheetsSaved.svg';
import colors from '@/theme/colors';
import { BodyMediumMidEmphasis, BodySmallMidEmphasis, TitleMediumEmphasized, TitleSmall } from '@/design-system';
import { GET_DISTRICT, GET_SCHOOL } from '@/graphql/queries';
import { GetDistrictResponse, GetSchoolResponse } from '@/graphql/types';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';


interface EmbedHeaderProps {
  districtId?: string | null;
  audienceId?: string | null;
  audienceType?: string | null;
}

export const EmbedHeader: React.FC<EmbedHeaderProps> = ({
  districtId,
  audienceId,
  audienceType,
}) => {
  const { data: schoolData } = useQuery<GetSchoolResponse>(GET_SCHOOL, {
    variables: {
      schoolId: Number(audienceId),
    },
    skip: audienceType !== 'school',
  });

  const { data: districtData } = useQuery<GetDistrictResponse>(GET_DISTRICT, {
    variables: {
      districtId: Number(districtId),
    },
  });

  const formatCounter = (value: number) => {
    const needsThousandScaling = !Number.isInteger(value);
    const actual = needsThousandScaling ? value * 1000 : value;
    return Math.round(actual).toLocaleString('en-US');
  };

  const counters = useMemo(() => {
    if (audienceType === 'school') {
      return {
        treeCounter: formatCounter(schoolData?.getSchool.treeCounter || 0),
        sheetCounter: formatCounter(schoolData?.getSchool.sheetCounter || 0),
      };
    }

    return {
      treeCounter: formatCounter(districtData?.getDistrict.treeCounter || 0),
      sheetCounter: formatCounter(districtData?.getDistrict.sheetCounter || 0),
    };
  }, [audienceType, schoolData, districtData]);

  const institutionName =
    audienceType === 'school' ? schoolData?.getSchool.name : districtData?.getDistrict.name;

  return (
    <div className={cn.container}>
      <div className={cn.districtContainer}>
        <img src={peachjarLogo} alt="Peachjar Logo" width="107px" height="34px" />
        <div className={cn.divider} />
        <TitleMediumEmphasized>{institutionName}</TitleMediumEmphasized>
      </div>
      <div className={cn.savedTreesContainer}>
        <img src={sheetsSaved} alt="Sheets Saved" />
        <div className={cn.treeContainer}>
          <BodyMediumMidEmphasis>Sheets Saved</BodyMediumMidEmphasis>
          <BodySmallMidEmphasis color={colors.soil}>
            {counters.sheetCounter}
          </BodySmallMidEmphasis>
        </div>
        <div>
          <TitleSmall>=</TitleSmall>
        </div>
        <div className={cn.treeContainer}>
          <BodyMediumMidEmphasis>Trees Saved</BodyMediumMidEmphasis>
          <BodySmallMidEmphasis color={colors.soil}>
            {counters.treeCounter}
          </BodySmallMidEmphasis>
        </div>
      </div>
    </div>
  );
};


const cn = {
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-bottom: solid 2px #e6e6e6;
    padding: 24px 16px 24px;
    width: 100%;
    background-color: ${colors.white};
  `,
  districtContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `,
  divider: css`
    width: 1px;
    height: 34px;
    background-color: ${colors.stone};
  `,
  treeContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  savedTreesContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `,
};

 